/**
 * AppHeader - Cabecera compartida de la aplicación
 * 
 * Extraída de MainApp para poder reutilizarla en las rutas independientes
 */

import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { useAuth } from '../AuthContext';
import { usePlaylistsContext } from '../contexts/PlaylistsContext';
import { SyncStatusBadge } from './SyncStatusBadge';
import { SpotifyQueueBadge } from './SpotifyQueueBadge';
import { TodayBadge } from './TodayBadge';
import { LogOut, Calendar, CheckSquare, TrendingUp, Sparkles, Music2, LayoutDashboard, ListMusic, Plus } from 'lucide-react';

const AppHeader = ({ 
  view, 
  setView, 
  isLiveMode = false,
  onNewTask,
  fetchPrecios,
  onOpenDossier,
  adminNavSlot,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { playlists } = usePlaylistsContext();

  // Detectar vista activa basándose en ruta o estado view
  const getActiveView = () => {
    if (location.pathname === '/reports') return 'reports';
    if (location.pathname === '/calendar') return 'calendar';
    if (location.pathname === '/tasks') return 'tasks';
    if (location.pathname === '/trends') return 'tendencias';
    if (location.pathname === '/team') return 'team';
    if (location.pathname === '/finances') return 'finanzas';
    if (location.pathname === '/presupuestos') return 'presupuestos';
    if (location.pathname === '/users') return 'users';
    if (location.pathname === '/precios') return 'precios';
    if (location.pathname === '/plantillas') return 'plantillas';
    if (location.pathname === '/settings') return 'settings';
    if (location.pathname === '/dashboard') return 'dashboard';
    if (location.pathname === '/library') return 'library';
    return view;
  };

  const activeView = getActiveView();

  // Mapeo de vistas a rutas de React Router
  const viewToRouteMap = {
    'dashboard': '/',
    'calendar': '/calendar',
    'tendencias': '/trends',
    'tasks': '/tasks',
    'reports': '/reports',
    'team': '/team',
    'finanzas': '/finances',
    'presupuestos': '/presupuestos',
    'users': '/users',
    'precios': '/precios',
    'plantillas': '/plantillas',
    'library': '/'
  };

  const handleNavigation = (targetView) => {
    // Caso especial: Dossier abre un modal, no una ruta
    if (targetView === 'dossier') {
      if (onOpenDossier) {
        onOpenDossier();
      } else {
        navigate('/?openDossier=true');
      }
      return;
    }
    
    // Library siempre vive dentro de MainApp (no tiene ruta propia)
    if (targetView === 'library') {
      if (location.pathname !== '/') {
        navigate('/');
        if (setView) setTimeout(() => setView('library'), 150);
      } else if (setView) {
        setView('library');
      }
      return;
    }
    
    // Si la vista tiene una ruta de React Router, navegar directamente
    if (viewToRouteMap[targetView]) {
      navigate(viewToRouteMap[targetView]);
      return;
    }
    
    // Vistas que están en MainApp pero no tienen ruta independiente (playlists, playlist-detail)
    if (targetView === 'playlists' || targetView === 'playlist-detail') {
      navigate(`/?view=${targetView}`);
      return;
    }
    
    // Fallback para otras vistas legacy
    if (location.pathname !== '/') {
      navigate('/');
      if (setView) {
        setTimeout(() => setView(targetView), 150);
      }
    } else if (setView) {
      setView(targetView);
    }
  };

  return (
    <div className="mb-3 sm:mb-4">
      {/* Logo and title row */}
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <img 
            src="/logo.png" 
            alt="Luis Herrera Eventos" 
            className="h-12 sm:h-20 md:h-24 w-auto flex-shrink-0 cursor-pointer"
            onClick={() => handleNavigation('dashboard')}
          />
          <h1 
            className="text-sm sm:text-xl md:text-2xl font-bold text-white truncate cursor-pointer" 
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            onClick={() => handleNavigation('dashboard')}
          >
            <span className="hidden md:inline">Luis Herrera Eventos DJ TEAM</span>
            <span className="hidden sm:inline md:hidden">Luis Herrera</span>
            <span className="sm:hidden">LH Eventos</span>
          </h1>
        </div>
        <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
          <span className="text-slate-300 text-sm hidden lg:block">
            {user?.username}
          </span>
          <TodayBadge />
          <SpotifyQueueBadge />
          <SyncStatusBadge />
          <Button
            onClick={logout}
            variant="outline"
            size="sm"
            className="bg-slate-700 border-slate-600 text-white hover:bg-slate-600"
          >
            <LogOut className="w-4 h-4 md:mr-2" />
            <span className="hidden md:inline">Salir</span>
          </Button>
        </div>
      </div>
      
      {/* Admin nav slot — coloca AdminNavButtons (Equipo/Usuarios/Precios…)
          ENTRE el logo y los main nav buttons, para que el orden sea idéntico
          al de MainApp y no se "invierta" entre rutas (fix bug nav rows). */}
      {adminNavSlot}

      {/* Main navigation buttons */}
      <div className="flex flex-wrap items-center gap-1 sm:gap-2">
        <Button
          onClick={() => handleNavigation('dashboard')}
          className={`flex-shrink-0 text-xs sm:text-sm ${activeView === 'dashboard' ? 'bg-violet-600' : 'bg-slate-700'}`}
          size="sm"
        >
          <LayoutDashboard className="w-4 h-4 sm:mr-2" />
          <span className="hidden sm:inline">Dashboard</span>
        </Button>
        <Button
          onClick={() => handleNavigation('library')}
          className={`flex-shrink-0 text-xs sm:text-sm ${activeView === 'library' ? 'bg-violet-600' : 'bg-slate-700'}`}
          size="sm"
        >
          <Music2 className="w-4 h-4 sm:mr-2" />
          <span className="hidden sm:inline">Biblioteca</span>
        </Button>
        <Button
          onClick={() => handleNavigation('playlists')}
          className={`flex-shrink-0 text-xs sm:text-sm ${activeView === 'playlists' ? 'bg-violet-600' : 'bg-slate-700'}`}
          size="sm"
        >
          <ListMusic className="w-4 h-4 sm:mr-2" />
          <span className="hidden sm:inline">Eventos</span>
          {playlists?.length > 0 && (
            <span className="ml-1 bg-slate-600 text-xs px-1.5 rounded-full">
              {playlists.length}
            </span>
          )}
        </Button>
        
        {!isLiveMode && (
          <>
            <Button
              onClick={() => handleNavigation('calendar')}
              className={`flex-shrink-0 text-xs sm:text-sm ${activeView === 'calendar' ? 'bg-violet-600' : 'bg-slate-700'}`}
              size="sm"
            >
              <Calendar className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Calendario</span>
            </Button>
            {user?.role === 'admin' && (
              <Button
                onClick={() => handleNavigation('tasks')}
                className={`flex-shrink-0 text-xs sm:text-sm ${activeView === 'tasks' ? 'bg-violet-600' : 'bg-slate-700'}`}
                size="sm"
              >
                <CheckSquare className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Tareas</span>
              </Button>
            )}
            {user?.role === 'admin' && (
              <Button
                onClick={() => {
                  if (onNewTask) {
                    onNewTask();
                  } else {
                    // Navegar a MainApp con el formulario de tarea abierto
                    navigate('/?openTaskForm=true');
                  }
                }}
                className="flex-shrink-0 text-xs sm:text-sm bg-violet-600 hover:bg-violet-700"
                size="sm"
                title="Nueva Tarea"
              >
                <Plus className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Nueva Tarea</span>
              </Button>
            )}
            {user?.role === 'admin' && (
              <Button
                onClick={() => handleNavigation('reports')}
                className={`flex-shrink-0 text-xs sm:text-sm ${activeView === 'reports' ? 'bg-violet-600' : 'bg-slate-700'}`}
                size="sm"
              >
                <TrendingUp className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Informes</span>
              </Button>
            )}
          </>
        )}
        <Button
          onClick={() => handleNavigation('tendencias')}
          className={`flex-shrink-0 ${isLiveMode ? 'text-base sm:text-lg px-6 py-6' : 'text-xs sm:text-sm'} ${activeView === 'tendencias' ? 'bg-violet-600' : 'bg-slate-700'}`}
          size={isLiveMode ? "lg" : "sm"}
        >
          <Sparkles className={`${isLiveMode ? 'w-6 h-6' : 'w-4 h-4'} sm:mr-2`} />
          <span className={isLiveMode ? 'inline' : 'hidden sm:inline'}>Tendencias</span>
        </Button>
      </div>
    </div>
  );
};

export default AppHeader;
